import React, { useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'

import Rightbar from 'pages/main/cheques/Rightbar'
import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { StandardFields, FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsOptions from 'pages/main/analytics/parts/useAnalyticsOptions'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import ChainFilterBar, { useChainFilters, FilterField, FilterSelect, ExcelButton } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	money, percent, intensityStyle, heatStyle, columnStats, buildRanks, chainColor,
	RankBadge, FillCell, useSort, SortTh, ChainKpi, HeatLegend, ChainEmpty, TableSearch,
} from '../parts/chainUi'

// Показатель в ячейках матрицы
const METRICS = ['quantity', 'revenue', 'profit']

// Цвет ABC-группы: A — основная выручка, C — хвост ассортимента
const ABC_STYLE = {
	A: { background: 'rgba(18, 184, 134, 0.22)', color: '#0f9f74' },
	B: { background: 'rgba(245, 159, 0, 0.22)', color: '#c77c02' },
	C: { background: 'rgba(240, 101, 72, 0.16)', color: '#e0492f' },
}

function qty(value) {
	return formatMoney(Number(value || 0), 3)
}

function ProductSales() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [metric, setMetric] = useState('revenue')
	const [search, setSearch] = useState('')
	const [details, setDetails] = useState(null)
	const [showRightBar, setShowRightBar] = useState(false)
	const [selectedCheque, setSelectedCheque] = useState({})

	const filters = useChainFilters({ order_type_ids: [], source_ids: [], menu_ids: [], category_ids: [] }, searchWithFilter)
	const { draft, patch, applied } = filters
	const options = useAnalyticsOptions(['sources', 'menus', 'categories'])

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/product-sales', filter), {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const branches = useMemo(() => data?.branches || [], [data])
	const totals = data?.totals || {}
	const branchById = useMemo(() => Object.fromEntries(branches.map(item => [item.pos_id, item])), [branches])

	const format = (value) => metric === 'quantity' ? qty(value) : money(value)
	const cellValue = (product, posId) => Number(product.poses?.[posId]?.[metric] || 0)

	const products = useMemo(() => {
		const list = data?.products || []
		const query = search.trim().toLowerCase()
		if (!query) return list
		return list.filter(item => `${item.product_name} ${item.category_name || ''} ${item.menu_name || ''}`.toLowerCase().includes(query))
	}, [data, search])

	// Место — по итогу сети в выбранном показателе (по всему списку, а не по найденному)
	const { ranks, count } = useMemo(() => buildRanks(data?.products || [], metric, true, 'product_id'), [data, metric])

	const readSortValue = (row, key) => key.startsWith('pos:') ? cellValue(row, Number(key.slice(4))) : row[key]
	const { sorted, sort, toggle } = useSort(products, 'revenue', 'desc', readSortValue)
	const branchStats = useMemo(() => columnStats(branches, metric), [branches, metric])

	const topProduct = useMemo(() => {
		const list = data?.products || []
		return list.length > 0 ? [...list].sort((a, b) => b[metric] - a[metric])[0] : null
	}, [data, metric])
	const abc = data?.abc || {}

	async function openDetails(product, posId) {
		const response = await GET(buildAnalyticsUrl('/chain/report/product-sales-cheques', applied, {
			pos_id: posId || null,
			product_id: product.product_id,
		}), {}, { loader: true })
		if (httpOk(response)) {
			setDetails({
				...response.data,
				product_name: product.product_name,
				unit_name: product.unit_name,
				pos_name: posId ? branchById[posId]?.pos_name : t('chain_all_branches'),
			})
		}
	}

	function closeDetails() {
		setDetails(null)
		setShowRightBar(false)
		setSelectedCheque({})
	}

	async function openCheque(row) {
		const response = await GET(`/cheques/${row.id}`, {}, { loader: true })
		if (httpOk(response)) {
			setSelectedCheque(response.data.data)
			setShowRightBar(true)
		}
	}

	function orderTypeName(value) {
		if (Number(value) === 1) return t('hall')
		if (Number(value) === 2) return t('takeaway')
		if (Number(value) === 3) return t('delivery')
		return '—'
	}

	const metricLabels = {
		quantity: t('quantity'),
		revenue: t('revenue'),
		profit: t('chain_gross_profit'),
	}

	function exportExcel() {
		if (!data) return
		const period = `${t('period')}: ${periodText(data.start_date, data.end_date)}`
		const list = [...(data.products || [])].sort((a, b) => b[metric] - a[metric])
		const type = metric === 'quantity' ? 'number' : 'money'
		const byMetric = buildRanks(list, metric, true, 'product_id').ranks

		const longRows = []
		list.forEach(product => {
			branches.forEach(branch => {
				const cell = product.poses?.[branch.pos_id]
				if (!cell) return
				longRows.push([
					product.product_name, product.category_name || '', product.menu_name || '', product.unit_name || '',
					branch.pos_name, cell.quantity, cell.revenue, cell.profit,
				])
			})
		})

		downloadExcel(`${t('chain_product_sales_title')} ${periodText(data.start_date, data.end_date)}`, [
			{
				name: metricLabels[metric],
				title: `${t('chain_product_sales_title')} — ${metricLabels[metric]}`,
				subtitle: period,
				columns: [
					{ label: t('chain_rank'), type: 'int', width: 7 },
					{ label: t('chain_dish'), type: 'text', width: 34 },
					{ label: t('category'), type: 'text', width: 20 },
					{ label: t('unit'), type: 'text', width: 8 },
					...branches.map(branch => ({ label: branch.pos_name, type })),
					{ label: t('total'), type },
					{ label: t('chain_revenue_share'), type: 'percent', width: 11 },
					{ label: 'ABC', type: 'text', width: 6 },
				],
				rows: list.map(product => [
					byMetric[product.product_id] || '', product.product_name, product.category_name || '', product.unit_name || '',
					...branches.map(branch => cellValue(product, branch.pos_id)),
					product[metric], product.share, product.abc,
				]),
				footer: [
					'', t('total'), '', '',
					...branches.map(branch => branch[metric]),
					totals[metric], 100, '',
				],
			},
			{
				name: t('chain_all_metrics'),
				title: `${t('chain_product_sales_title')} — ${t('chain_all_metrics')}`,
				subtitle: period,
				columns: [
					{ label: t('chain_dish'), type: 'text', width: 34 },
					{ label: t('category'), type: 'text', width: 20 },
					{ label: t('menu'), type: 'text', width: 18 },
					{ label: t('unit'), type: 'text', width: 8 },
					{ label: t('chain_branch'), type: 'text', width: 26 },
					{ label: t('quantity'), type: 'number' },
					{ label: t('revenue'), type: 'money' },
					{ label: t('chain_gross_profit'), type: 'money' },
				],
				rows: longRows,
				footer: [t('total'), '', '', '', '', totals.quantity, totals.revenue, totals.profit],
			},
		])
	}

	function exportDetails() {
		if (!details) return
		downloadExcel(`${details.product_name} — ${details.pos_name}`, [{
			name: t('cheques'),
			title: `${details.product_name} — ${details.pos_name}`,
			subtitle: `${t('period')}: ${periodText(applied.start_date, applied.end_date)}`,
			columns: [
				{ label: t('close_date'), type: 'text', width: 18 },
				{ label: t('chain_branch'), type: 'text', width: 24 },
				{ label: t('chain_cheque_no'), type: 'text', width: 10 },
				{ label: t('order_type'), type: 'text', width: 12 },
				{ label: t('cashier'), type: 'text', width: 18 },
				{ label: t('waiter'), type: 'text', width: 18 },
				{ label: t('quantity'), type: 'number', width: 10 },
				{ label: t('revenue'), type: 'money' },
				{ label: t('chain_gross_profit'), type: 'money' },
				{ label: t('chain_cheque_amount'), type: 'money' },
			],
			rows: details.cheques.map(row => [
				formatDate(row.cheque_closed_at, 'dd.MM.yyyy HH:mm'), row.pos_name, row.cheque_number, orderTypeName(row.order_type),
				row.cashier_name || '', row.waiter_name || '', row.quantity, row.revenue, row.profit, row.cheque_amount,
			]),
			footer: [t('total'), '', '', '', '', '', details.totals.quantity, details.totals.revenue, '', ''],
		}])
	}

	return (
		<>
			<ChainFilterBar
				title={t('chain_product_sales_title')}
				icon="uil-utensils"
				filters={filters}
				wide
				onExcel={exportExcel}
				excelDisabled={!data}
			>
				<FilterField label={t('menu')}>
					<FilterSelect isMulti options={options.menus} allLabel={t('chain_all_menus')} value={draft.menu_ids} onChange={(ids) => patch({ menu_ids: ids })} />
				</FilterField>
				<FilterField label={t('categories')}>
					<FilterSelect isMulti options={options.categories} allLabel={t('chain_all_categories')} value={draft.category_ids} onChange={(ids) => patch({ category_ids: ids })} />
				</FilterField>
				<StandardFields filters={filters} options={options} branch={false} />
			</ChainFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_dishes_revenue')} icon="uil-money-bill" color="#12b886"
						value={money(totals.revenue)}
						sub={<>{t('chain_gross_profit')} <b>{money(totals.profit)}</b> · {t('margin')} <b>{percent(totals.margin)}</b></>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_sold_quantity')} icon="uil-shopping-basket" color="#5b73e8"
						value={qty(totals.quantity)}
						sub={<>{formatMoney(totals.products || 0)} {t('chain_dishes_lower')}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_top_dish')} icon="uil-trophy" color="#f59f00"
						value={topProduct ? topProduct.product_name : '—'}
						sub={topProduct ? <>{format(topProduct[metric])} · {percent(topProduct.share)} {t('chain_of_revenue')}</> : null} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_abc_group_a')} icon="uil-star" color="#7c4dff"
						value={<>{formatMoney(abc.A?.count || 0)} {t('chain_dishes_lower')}</>}
						title2={t('chain_abc_hint')}
						sub={<>{percent(abc.A?.share)} {t('chain_of_revenue')} · B {formatMoney(abc.B?.count || 0)} · C {formatMoney(abc.C?.count || 0)}</>} />
				</div>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_dishes_by_branches')}</h5>
							<FilterSegment
								options={METRICS.map(id => ({ id, label: metricLabels[id] }))}
								value={metric}
								onChange={setMetric}
							/>
						</div>
						<div className="chain-toolbar-group">
							<HeatLegend mode="success" />
							<TableSearch value={search} onChange={setSearch} placeholder={t('chain_search_dish')} />
						</div>
					</div>

					<div className="chain-scroll">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky" title={t('chain_rank')}>#</th>
									<SortTh sortKey="product_name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('chain_dish')}</SortTh>
									{branches.map((branch, index) => (
										<SortTh key={branch.pos_id} sortKey={`pos:${branch.pos_id}`} sort={sort} onSort={toggle} className="text-end">
											<span className="chain-dot" style={{ background: chainColor(index) }}></span>
											{branch.pos_name}
										</SortTh>
									))}
									<SortTh sortKey={metric} sort={sort} onSort={toggle} className="text-end chain-total-col">{t('total')}</SortTh>
									<SortTh sortKey="share" sort={sort} onSort={toggle} className="text-end" title={t('chain_revenue_share_hint')}>{t('chain_revenue_share')}</SortTh>
									<SortTh sortKey="abc" sort={sort} onSort={toggle} className="text-center" title={t('chain_abc_hint')}>ABC</SortTh>
								</tr>
							</thead>
							<tbody>
								{sorted.length === 0 && <ChainEmpty colSpan={branches.length + 5} />}
								{sorted.map(product => {
									const values = branches.map(branch => cellValue(product, branch.pos_id))
									const rowMax = Math.max(0, ...values)
									return (
										<tr key={product.product_id}>
											<td className="chain-index chain-sticky"><RankBadge rank={ranks[product.product_id]} count={count} /></td>
											<td className="chain-name chain-sticky-2 chain-click" onClick={() => openDetails(product, null)} title={t('chain_open_details')}>
												<span>{product.product_name}</span>
												<div className="chain-muted">{[product.category_name, product.unit_name].filter(Boolean).join(' · ') || '—'}</div>
											</td>
											{branches.map((branch, index) => {
												const value = values[index]
												const cell = product.poses?.[branch.pos_id]
												const isMax = value > 0 && value === rowMax && branches.length > 1
												return (
													<td key={branch.pos_id}
														className={`chain-num ${cell ? 'chain-click' : ''}`}
														style={intensityStyle(value, rowMax, 'success')}
														title={cell ? `${branch.pos_name}\n${t('quantity')}: ${qty(cell.quantity)}\n${t('revenue')}: ${money(cell.revenue)}\n${t('chain_gross_profit')}: ${money(cell.profit)}` : undefined}
														onClick={() => cell && openDetails(product, branch.pos_id)}>
														<span style={{ fontWeight: isMax ? 800 : 400, color: value < 0 ? '#f06548' : undefined }}>
															{cell ? format(value) : '—'}
														</span>
													</td>
												)
											})}
											<td className="chain-num chain-total-col" style={{ color: product[metric] < 0 ? '#f06548' : undefined }}>{format(product[metric])}</td>
											<FillCell percent={data?.products?.[0]?.share > 0 ? product.share / data.products[0].share * 100 : 0} tone="success">{percent(product.share)}</FillCell>
											<td className="text-center fw-bold" style={ABC_STYLE[product.abc]}>{product.abc}</td>
										</tr>
									)
								})}
							</tbody>
							{sorted.length > 0 && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('total')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className="chain-num" style={heatStyle(branch[metric], branchStats)}>{format(branch[metric])}</td>
										))}
										<td className="chain-num chain-total-col">{format(totals[metric])}</td>
										<td className="chain-num">100%</td>
										<td></td>
									</tr>
								</tfoot>
							)}
						</table>
					</div>
					<div className="chain-muted mt-2">
						<i className="uil uil-info-circle me-1"></i>
						{t('chain_product_sales_hint')}
					</div>
				</div>
			</div>

			<TopChart products={data?.products || []} branches={branches} metric={metric} metricLabel={metricLabels[metric]} />

			{/* MODAL DETAILS */}
			<Modal show={!!details} onHide={closeDetails} size="xl" centered scrollable enforceFocus={false}>
				<Modal.Header closeButton>
					<Modal.Title>
						{details?.product_name} — {details?.pos_name}
						<div className="chain-muted fw-normal">
							{periodText(applied.start_date, applied.end_date)} · {formatMoney(details?.cheques?.length || 0)} {t('chain_cheques_lower')} · {qty(details?.totals?.quantity)} {details?.unit_name || ''} · {money(details?.totals?.revenue)}
						</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					{details?.limited && (
						<div className="chain-note mb-2"><i className="uil uil-exclamation-triangle"></i>{t('chain_details_limited')}</div>
					)}
					<table className="chain-table">
						<thead>
							<tr>
								<th>#</th>
								<th>{t('close_date')}</th>
								<th>{t('chain_branch')}</th>
								<th>{t('chain_cheque_no')}</th>
								<th>{t('order_type')}</th>
								<th>{t('cashier')}</th>
								<th>{t('waiter')}</th>
								<th className="text-end">{t('quantity')}</th>
								<th className="text-end">{t('revenue')}</th>
								<th className="text-end">{t('chain_gross_profit')}</th>
								<th className="text-end">{t('chain_cheque_amount')}</th>
							</tr>
						</thead>
						<tbody>
							{(details?.cheques || []).length === 0 && <ChainEmpty colSpan={11} />}
							{(details?.cheques || []).map((row, index) => (
								<tr key={row.id} className={`chain-click ${showRightBar && selectedCheque.id === row.id ? 'table-tr-active' : ''}`}
									onClick={() => openCheque(row)} title={t('chain_open_cheque')}>
									<td className="chain-index">{index + 1}</td>
									<td className="text-nowrap">{formatDate(row.cheque_closed_at, 'dd.MM.yy HH:mm')}</td>
									<td>{row.pos_name}</td>
									<td>{row.cheque_number}</td>
									<td>{orderTypeName(row.order_type)}</td>
									<td>{row.cashier_name || '—'}</td>
									<td>{row.waiter_name || '—'}</td>
									<td className="chain-num">{qty(row.quantity)}</td>
									<td className="chain-num"><b>{money(row.revenue)}</b></td>
									<td className="chain-num" style={{ color: row.profit < 0 ? '#f06548' : undefined }}>{money(row.profit)}</td>
									<td className="chain-num">{money(row.cheque_amount)}</td>
								</tr>
							))}
						</tbody>
					</table>

					{(details?.returns || []).length > 0 && (
						<>
							<h6 className="mt-3 mb-2">
								{t('chain_returns')}: {qty(details.totals.returns_quantity)} {details.unit_name || ''} · −{money(details.totals.returns_revenue)}
							</h6>
							<table className="chain-table">
								<thead>
									<tr>
										<th>#</th>
										<th>{t('date')}</th>
										<th>{t('chain_branch')}</th>
										<th>{t('chain_cheque_no')}</th>
										<th>{t('cashier')}</th>
										<th>{t('comment')}</th>
										<th className="text-end">{t('quantity')}</th>
										<th className="text-end">{t('amount')}</th>
									</tr>
								</thead>
								<tbody>
									{details.returns.map((row, index) => (
										<tr key={`${row.id}-${index}`} className="chain-click" onClick={() => openCheque({ id: row.cheque_id })} title={t('chain_open_cheque')}>
											<td className="chain-index">{index + 1}</td>
											<td className="text-nowrap">{formatDate(row.created_at, 'dd.MM.yy HH:mm')}</td>
											<td>{row.pos_name}</td>
											<td>{row.cheque_number}</td>
											<td>{row.cashier_name || '—'}</td>
											<td>{row.comment || '—'}</td>
											<td className="chain-num">{qty(row.quantity)}</td>
											<td className="chain-num" style={{ color: '#f06548' }}>−{money(row.revenue)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<ExcelButton onClick={exportDetails} disabled={!details?.cheques?.length} />
					<button type="button" className="btn btn-outline-secondary" onClick={closeDetails}>{t('close')}</button>
				</Modal.Footer>
			</Modal>

			<Rightbar
				showRightBar={showRightBar}
				setShowRightBar={setShowRightBar}
				selectedItem={selectedCheque}>
			</Rightbar>
		</>
	)
}

// Топ-15 блюд сети: из чего складывается итог — стопкой по филиалам
function TopChart({ products, branches, metric, metricLabel }) {
	const { t } = useTranslation()
	const TOP = 15
	const top = [...products].sort((a, b) => b[metric] - a[metric]).slice(0, TOP)
	if (top.length === 0) return null

	const series = branches.map(branch => ({
		name: branch.pos_name,
		data: top.map(product => Math.round(Number(product.poses?.[branch.pos_id]?.[metric] || 0) * 1000) / 1000),
	}))
	const formatValue = value => metric === 'quantity' ? qty(value) : formatMoney(Math.round(value))

	const options = {
		chart: { type: 'bar', stacked: true, toolbar: { show: false } },
		plotOptions: { bar: { horizontal: true, borderRadius: 2, barHeight: '64%' } },
		colors: branches.map((branch, index) => chainColor(index)),
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: {
			categories: top.map(product => product.product_name),
			labels: { formatter: value => metric === 'quantity' ? formatMoney(Math.round(value)) : formatMoney(Math.round(value / 1000)) + 'k' },
		},
		yaxis: { labels: { maxWidth: 220 } },
		tooltip: { y: { formatter: formatValue } },
	}

	return (
		<div className="card chain-card mb-3">
			<div className="card-body">
				<div className="chain-card-title"><h5>{t('chain_top_dishes')} — {metricLabel}</h5></div>
				<ReactApexChart key={metric} type="bar" height={Math.max(320, top.length * 30 + 90)} series={series} options={options} />
			</div>
		</div>
	)
}

export default ProductSales
